/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { MessageType, } from "@fluidframework/driver-definitions/internal";
import { Quorum } from "./quorum.js";
/**
 * Handles protocol specific ops.
 * @internal
 */
export class ProtocolOpHandler {
    get quorum() {
        return this._quorum;
    }
    constructor(minimumSequenceNumber, sequenceNumber, members, proposals, values, sendProposal) {
        this.minimumSequenceNumber = minimumSequenceNumber;
        this.sequenceNumber = sequenceNumber;
        this._quorum = new Quorum(members, proposals, values, sendProposal);
    }
    get attributes() {
        return {
            minimumSequenceNumber: this.minimumSequenceNumber,
            sequenceNumber: this.sequenceNumber,
        };
    }
    setConnectionState(connected, clientId) {
        this._quorum.setConnectionState(connected, clientId);
    }
    snapshot() {
        return this._quorum.snapshot();
    }
    close() {
        this._quorum.close();
    }
    processMessage(message, local) {
        // verify it's moving sequentially
        if (message.sequenceNumber !== this.sequenceNumber + 1) {
            throw new Error(`Protocol state is not moving sequentially. ` +
                `Current is ${this.sequenceNumber}. Next is ${message.sequenceNumber}`);
        }
        // Update tracked sequence numbers
        this.sequenceNumber = message.sequenceNumber;
        this.minimumSequenceNumber = message.minimumSequenceNumber;
        let immediateNoOp = false;
        switch (message.type) {
            case MessageType.ClientJoin: {
                const systemJoinMessage = message;
                const join = JSON.parse(systemJoinMessage.data);
                const member = {
                    client: join.detail,
                    sequenceNumber: systemJoinMessage.sequenceNumber,
                };
                this._quorum.addMember(join.clientId, member);
                break;
            }
            case MessageType.ClientLeave: {
                const systemLeaveMessage = message;
                const clientId = JSON.parse(systemLeaveMessage.data);
                this._quorum.removeMember(clientId);
                break;
            }
            case MessageType.Propose: {
                // back-compat: ADO #1385: This should become unconditional eventually.
                // Can be done only after Container.processRemoteMessage() stops parsing content!
                if (typeof message.contents === "string") {
                    message.contents = JSON.parse(message.contents);
                }
                const proposal = message.contents;
                this._quorum.addProposal(proposal.key, proposal.value, message.sequenceNumber, local, message.clientSequenceNumber);
                // On a quorum proposal, immediately send a response to expedite the approval.
                immediateNoOp = true;
                break;
            }
            default:
        }
        // Notify the quorum of the MSN from the message. We rely on it to handle duplicate values but may
        // want to move that logic to this class.
        this._quorum.updateMinimumSequenceNumber(message);
        return { immediateNoOp };
    }
    /**
     * Gets the scribe protocol state
     * @param scrubUserData - whether to remove all user data from the quorum members. CAUTION: this will corrupt the quorum if used in a summary.
     */
    getProtocolState(scrubUserData = false) {
        // return a new object every time
        // this ensures future state changes will not affect outside callers
        const snapshot = this._quorum.snapshot();
        if (scrubUserData) {
            // In place, remove any identifying client information
            snapshot.members = snapshot.members.map(([id, sequencedClient]) => [
                id,
                {
                    ...sequencedClient,
                    client: {
                        ...sequencedClient.client,
                        user: { id: "" },
                    },
                },
            ]);
        }
        return {
            sequenceNumber: this.sequenceNumber,
            minimumSequenceNumber: this.minimumSequenceNumber,
            ...snapshot,
        };
    }
}
//# sourceMappingURL=protocol.js.map